import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Spinner } from './ui';

/**
 * Preview and Save as PDF, for any customer-facing document.
 *
 * Both the print view and the PDF sit behind the same bearer token as every
 * other call, so neither can be an `<a href>` or an `<iframe src>` — a browser
 * sends no Authorization header on either, and the old "Print" link quietly
 * 401'd. They are fetched with the token and handed to the page: the HTML as
 * `srcDoc`, the PDF as a blob.
 *
 * The invoice and the estimate share this because they are the same paper.
 */
export interface DocumentSource {
  /** "Invoice #1042" — the modal's heading. */
  label: string;
  /** "invoice-1042.pdf" — what the browser calls the download. */
  filename: string;
  html: () => Promise<string>;
  pdf: () => Promise<Blob>;
}

export function DocumentActions({
  doc,
  onError,
}: {
  doc: DocumentSource;
  onError?: (message: string) => void;
}) {
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  const savePdf = useCallback(async () => {
    setSaving(true);
    try {
      const blob = await doc.pdf();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.filename;
      document.body.append(link);
      link.click();
      link.remove();
      // Revoked on the next tick: Safari cancels the download if the URL dies
      // while the click is still being handled.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (error) {
      onError?.((error as { message?: string }).message ?? 'Could not build the PDF');
    } finally {
      setSaving(false);
    }
  }, [doc, onError]);

  return (
    <>
      <Button icon="file" onClick={() => setPreviewing(true)}>
        Preview
      </Button>
      <Button loading={saving} onClick={() => void savePdf()}>
        Save as PDF
      </Button>

      {previewing && (
        <DocumentPreview doc={doc} onClose={() => setPreviewing(false)} onError={onError} />
      )}
    </>
  );
}

/**
 * The document at full size, exactly as the customer receives it — the same
 * HTML the printer and the email attachment are made from, not a re-drawing
 * of it in app styles. A re-drawing is the thing that drifts.
 */
function DocumentPreview({
  doc,
  onClose,
  onError,
}: {
  doc: DocumentSource;
  onClose: () => void;
  onError?: (message: string) => void;
}) {
  const [html, setHtml] = useState<string | null>(null);

  /*
   * Fetched once, when the modal opens. Held in a ref rather than a dependency
   * because `doc` and the callbacks are fresh object literals on every render
   * of the page behind this — as dependencies they would refetch the document
   * every time anything on that page changed.
   */
  const latest = useRef({ doc, onClose, onError });
  latest.current = { doc, onClose, onError };

  useEffect(() => {
    let live = true;
    const { doc: source, onClose: close, onError: fail } = latest.current;
    source
      .html()
      .then((text) => {
        if (live) setHtml(text);
      })
      .catch((error: { message?: string }) => {
        if (!live) return;
        fail?.(error.message ?? 'Could not load the preview');
        close();
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      <div className="fixed inset-0 bg-ink/40 backdrop-blur-[1px]" onClick={onClose} aria-hidden />

      <div className="relative m-auto flex h-[92vh] w-full max-w-[940px] flex-col overflow-hidden rounded-card border border-line bg-surface shadow-[0_24px_60px_-20px_rgb(23_43_34/0.4)]">
        <header className="flex shrink-0 items-center justify-between gap-4 border-b border-line px-5 py-3">
          <div className="min-w-0">
            <h2 className="truncate font-display text-[16px] font-bold tracking-[-0.015em] text-ink">
              {doc.label}
            </h2>
            <p className="text-[12.5px] text-ink-4">What the customer sees</p>
          </div>
          <Button size="sm" icon="close" onClick={onClose}>
            Close
          </Button>
        </header>

        <div className="min-h-0 flex-1 bg-canvas">
          {html === null ? (
            <div className="grid h-full place-items-center">
              <Spinner className="size-5 text-ink-4" />
            </div>
          ) : (
            <iframe
              title={doc.label}
              srcDoc={html}
              sandbox="allow-scripts"
              className="size-full border-0 bg-white"
            />
          )}
        </div>
      </div>
    </div>
  );
}
